import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";

import {
  addTip,
  updateTip,
  fetchTips,
  getTipsCategoryAdmin,
  getTipsCriteriaAdmin,
  getTipsDeliveryMethodAdmin,
  deleteTip,
} from "slices/Tips/thunk";

import TipForm from "./TipForm";
import TipsContainer from "./Tipscontainer"; // your table wrapper
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import NewDeleteModal from "Components/Common/NewDeleteModal";
import { tipPriorityOptions } from "./TipsHelper";
import { DeliveryPlatformEnum, FrequencyEnum, PriorityEnum } from "./TipsEnum";
import { toast } from "react-toastify";

const selectTipsState = (state: RootState) => (state as any).tips;

const tipsSelector = createSelector(selectTipsState, (tips) => ({
  tipsData: tips.tipsData,
  loading: tips.loading,
  pagination: tips.pagination,

  categories: tips.categories,
  criteria: tips.criteria,
  deliveryMethods: tips.deliveryMethods,

}));


const TipsPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();

  const { tipsData, loading, pagination, categories, criteria, deliveryMethods } =
    useSelector(tipsSelector);

  const tipList = tipsData?.tips || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editTipData, setEditTipData] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTipId, setSelectedTipId] = useState<number | null>(null);

  useEffect(() => {
  dispatch(getTipsCategoryAdmin());
  dispatch(getTipsCriteriaAdmin());
  dispatch(getTipsDeliveryMethodAdmin());
}, [dispatch]);

  // Debounced fetch (500ms) - same as CoursePage pattern
  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        fetchTips({
          tip_id: 0,
          page,
          per_page: limit,
          category_id: 0,
          criteria_id: 0,
          delivery_method_id: 0,
          frequency: "",
          delivery_platform: "",
          priority: 0,
          start_date: "",
          end_date: "",
          searchkey: searchQuery,
        })
      );
    }, 500);

    return () => clearTimeout(handler);
  }, [dispatch, page, limit, searchQuery]);

  useEffect(() => setPage(1), [searchQuery]);

const handleDeleteClick = (id: number) => {
  setSelectedTipId(id);
  setDeleteModalOpen(true);
};


// const handleStatusToggle = async (id: number, newStatus: string) => {
//   await dispatch(changeTipStatus(id, newStatus));

//   dispatch(
//     fetchTips({
//       tip_id: 0,
            // page,
            // per_page: limit,
            // category_id: 0,
            // criteria_id: 0,
            // delivery_method_id: 0,
            // frequency: "",
            // delivery_platform: "",
            // priority: 0,
            // start_date: "",
            // end_date: "",
            // searchkey: searchQuery,
//     })
//   );
// };

  // Formik validation placed in page (same pattern as CoursePage)
  const validation = useFormik({
    initialValues: {
      tip_title: "",
      tip_content: "",
      category_id: "",
      frequency: FrequencyEnum.Daily,
      criteria_id: "",
      delivery_method_id: "",
      delivery_platform: DeliveryPlatformEnum.App,
      priority: PriorityEnum.Low,
      start_date: "",
      end_date: "",
    },

    validationSchema: Yup.object({
     tip_title: Yup.string()
    .required("Tip title is required")
    .matches(/^[a-zA-Z ]*$/, "Special characters and numbers are not allowed"),
    tip_content: Yup.string()
      .required("Tip content is required")
      .max(70, "Maximum 70 characters allowed"),
      

      category_id: Yup.string().required("Category is required"),
      criteria_id: Yup.string().required("Criteria is required"),
      delivery_method_id: Yup.string().required("Delivery method is required"),
      delivery_platform: Yup.string().required("Platform is required"),
      // priority: Yup.number().required("Priority is required"),
      // If you want start/end required only for Custom frequency,
      // add when() logic to schema inside TipForm if needed.
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        // close modal first (same CoursePage flow)
        setIsModalOpen(false);

        if (isEdit && editTipData) {
          await dispatch(updateTip(editTipData.id, values));
           toast.success("Tip updated successfully!");
        } else {
          await dispatch(addTip(values));
          toast.success("Tip added successfully!");
        }

        // refresh list after add/update (match CoursePage)
        dispatch(
          fetchTips({
            tip_id: 0,
            page,
            per_page: limit,
            category_id: 0,
            criteria_id: 0,
            delivery_method_id: 0,
            frequency: "",
            delivery_platform: "",
            priority: 0,
            start_date: "",
            end_date: "",
            searchkey: searchQuery,
          })
        );

        resetForm();
        setIsEdit(false);
        setEditTipData(null);
      } catch (err) {
        console.error("Error saving tip:", err);
      }
    },
  });

 const toggleModal = () => {
  if (!isModalOpen) {
    validation.resetForm();     
    setIsEdit(false);           
    setEditTipData(null);   
    dispatch(getTipsCategoryAdmin());
    dispatch(getTipsCriteriaAdmin());
    dispatch(getTipsDeliveryMethodAdmin());    
  }

  setIsModalOpen(!isModalOpen);
};


  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowClick = useCallback((row: any) => {
    // optional: highlight row or set selected id
  }, []);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditTipData(row);
      setIsModalOpen(true);
    
      // Prefill validation values (same pattern as CoursePage)
      validation.setValues({
        tip_title: row.tip_title || "",
        tip_content: row.tip_content || "",
        category_id: row.category_id || "",
        frequency: row.frequency || "",
        criteria_id: row.criteria_id || "",
        delivery_method_id: row.delivery_method_id || "",
        delivery_platform: row.delivery_platform?.toLowerCase(),
        priority: row.priority || "",
        start_date: row.start_date || "",
        end_date: row.end_date || "",
      });
    },
    [validation]
  );

  const filteredTips = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tipList;

    return tipList.filter(
      (t: any) =>
        t.tip_title?.toLowerCase().includes(q) ||
        t.tip_content?.toLowerCase().includes(q)
    );
  }, [tipList, searchQuery]);

  const formatDate = (d: string) => moment(d).format("DD MMM YYYY");

  const columns = useMemo(
  () => [
    { header: "Tip ID", accessorKey: "id" },
    { header: "Title", accessorKey: "tip_title" },
    { header: "Category", accessorKey: "category_name" },
    { header: "Content", accessorKey: "tip_content" },
    { header: "Criteria", accessorKey: "criteria_name" },
    { header: "Delivery Method", accessorKey: "delivery_method_name" },
  //   {
  //   header: "Priority",
  //   accessorKey: "priority",
  //   cell: (cell: any) => {
  //     const value = Number(cell.getValue());
  //     const option = tipPriorityOptions.find(opt => opt.value === value);
  //     return option ? option.label : "N/A";
  //   }
  // },
    { header: "Frequency", accessorKey: "frequency" },
    // {
    //   header: "Created On",
    //   accessorKey: "createdon",
    //   cell: (cell: any) => formatDate(cell.getValue()),
    // },
    // {
    //   header: "Status",
    //   accessorKey: "status",
    //   enableColumnFilter: false,
    //   cell: (cell: any) => {
    //     const value = cell.getValue();
    //     const lowerVal = value.toString().toLowerCase();

    //     // 🔹 Define color based on status
    //     const statusColor =
    //       lowerVal === "active" ? "#159E3A" : "#A65F00"; // Green for Active, Red for Inactive

    //     return (
    //       <select
    //         className="form-select form-select-sm"
    //         value={cell.getValue()}
    //         style={{
    //           width: "120px",
    //           border: `1px solid ${statusColor}`,
    //           borderRadius: "20px",
    //           color: statusColor,
    //           padding: "4px 20px",
    //           height: "26px",
    //           fontSize: "12px",
    //           textAlign: "center",
    //         }}
    //         onChange={(e) =>
    //           handleStatusToggle(cell.row.original.id, e.target.value)
    //         }
    //       >
    //         <option value="Active">Active</option>
    //         <option value="Inactive">Inactive</option>
    //       </select>
    //     );
    //   },
    // },
    {
      header: "Actions",
      accessorKey: "actions",
      enableColumnFilter: false,
      cell: (cellProps: any) => {
        const row = cellProps.row.original;

        return (
          <ul className="list-inline hstack gap-1 mb-0">
            <li className="list-inline-item" title="Edit">
              <Button
                color=""
                className="btn btn-icon btn-soft-primary btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(row);
                }}
              >
                <i className="ri-pencil-fill"></i>
              </Button>
            </li>

            {/* Delete */}
           <li className="list-inline-item" title="Delete">
            <Button
              color=""
              className="btn btn-icon btn-soft-danger btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.id);  
              }}
            >
              <i className="ri-delete-bin-line"></i>
            </Button>
          </li>
          </ul>
        );
      },
    },
  ],
  [handleEditClick, handleDeleteClick]
);

  document.title = "Tips";

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Tips" pageTitle={["Tip", "Tips"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={2}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={""}
                placeholder="Filter"
                disabled
              />
              <i
                className="ri-filter-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>

          <Col md={3}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Tips..."
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>

          <Col md={7} className="text-end d-flex justify-content-end gap-2 align-items-center">
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card id="tipsList" className="border-0 shadow-none" style={{ border: "none" }}>
              <CardBody className="pt-3" style={{ minHeight: "500px" }}>
                <div className="tab-content">
                  {!loading ? (
                    filteredTips?.length > 0 ? (
                      <TipsContainer
                        columns={columns}
                        data={filteredTips}
                        pagination={pagination}
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        onPageChange={(p) => setPage(p)}  
                        // setRowId={() => {}}
                        onRowClick={handleRowClick}
                        tableClass="align-middle border-bottom rounded-pill"
                        theadClass="bg-primary text-white border-top border-success"
                        trClass=""
                        divClass="table-responsive table-card"
                        // SearchPlaceholder="Search tips..."
                      />
                    ) : (
                      <div className="text-center p-4">No records found.</div>
                    )
                  ) : (
                    <Loader />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <NewDeleteModal
          show={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="Delete Tip"
          message="Are you sure you want to delete this tip?"
          onConfirm={async () => {
            if (selectedTipId) {
              setDeleteModalOpen(false);

              const tip_id = selectedTipId;
              setSelectedTipId(null);

              await dispatch(deleteTip(tip_id));
               toast.success("Tip deleted successfully!");

              await dispatch(
                fetchTips({
                  tip_id: 0,
                  page,
                  per_page: limit,
                  category_id: 0,
                  criteria_id: 0,
                  delivery_method_id: 0,
                  frequency: "",
                  delivery_platform: "",
                  priority: 0,
                  start_date: "",
                  end_date: "",
                  searchkey: searchQuery,
                })
              );
            }
          }}
        />
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalBody>
          <TipForm validation={validation} isEdit={isEdit} categories={categories} criteria={criteria} deliveryMethods={deliveryMethods} />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default TipsPage;
